import {
  ghostBtnClass,
  mfgConfirmDialogClass,
  mfgPrimaryBtnClass,
} from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { CountClosePreview } from "@/lib/manufacturing/inventoryCountClosePreview";

type Props = {
  open: boolean;
  busy: boolean;
  preview: CountClosePreview;
  onCancel: () => void;
  onAccept: () => void;
};

export function ManufacturingInventoryCountCloseConfirm({
  open,
  busy,
  preview,
  onCancel,
  onAccept,
}: Props) {
  if (!open) return null;

  const { changes, skipped_uncounted, skipped_unchanged } = preview;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="inventory-count-accept-title"
        className={`max-w-lg w-full max-h-[min(80vh,560px)] flex flex-col ${mfgConfirmDialogClass}`}
      >
        <h3 id="inventory-count-accept-title" className="text-base font-semibold text-gray-900 dark:text-zinc-50">
          Accept these count changes?
        </h3>
        <p className="mt-1 text-xs text-gray-600 dark:text-zinc-300">
          Submitting closes the session and posts stock adjustments for the deltas below
          (uncounted lines are left unchanged).
        </p>

        <div className="mt-3 min-h-0 flex-1 overflow-y-auto rounded-lg border border-gray-200 dark:border-zinc-700">
          {changes.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-600 dark:text-zinc-300">
              No quantity changes to post
              {skipped_unchanged > 0 ? ` (${skipped_unchanged} counted with no delta)` : ""}.
              You can still submit to close the session.
            </p>
          ) : (
            <ul className="divide-y divide-gray-100 text-sm dark:divide-zinc-800">
              {changes.map((c) => (
                <li key={c.line_id} className="flex flex-wrap items-baseline justify-between gap-2 px-3 py-2">
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-zinc-50">{c.label}</div>
                    <div className="font-mono text-[11px] text-gray-500 dark:text-zinc-400">{c.code}</div>
                  </div>
                  <div className="shrink-0 text-right text-xs text-gray-700 dark:text-zinc-200">
                    <div>
                      {c.expected_qty} → {c.counted_qty}
                    </div>
                    <div
                      className={
                        c.quantity_delta > 0
                          ? "font-semibold text-emerald-700 dark:text-emerald-300"
                          : "font-semibold text-amber-700 dark:text-amber-300"
                      }
                    >
                      {c.quantity_delta > 0 ? "+" : ""}
                      {c.quantity_delta}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <p className="mt-2 text-[11px] text-gray-500 dark:text-zinc-400">
          {changes.length} change{changes.length === 1 ? "" : "s"}
          {skipped_uncounted > 0 ? ` · ${skipped_uncounted} uncounted skipped` : ""}
          {skipped_unchanged > 0 ? ` · ${skipped_unchanged} unchanged` : ""}
        </p>

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className={ghostBtnClass} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={mfgPrimaryBtnClass}
            onClick={onAccept}
            disabled={busy}
            autoFocus
          >
            {busy ? "Submitting…" : "Accept & close"}
          </button>
        </div>
      </div>
    </div>
  );
}
